import { useMutation } from "@apollo/client/react";
import { Dispatch, FC, MouseEventHandler, SetStateAction, useState } from "react";

import { DateRange } from "..";
import { parseGQLResponseError } from "../../graphql/util";
import { FilterPublishedImports } from "../common/FilterPublishedArtifacts";
import type { HandleType } from "../common/Formatters";
import IconInfo from "../common/IconInfo";
import { CancelLinkButton } from "../Filters";

import { Alert, AlertTitle, Button, Grid, Stack, styled, Typography } from "@mui/material";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import { CaretCircleRightIcon } from "@phosphor-icons/react/dist/csr/CaretCircleRight";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { differenceInDays, endOfDay, format, toDate } from "date-fns";
import Permissions from "../../constants/permissions";
import { DOWNLOAD_FILES_QUERY } from "../../graphql";
import {
  BackfillDocument,
  BackfillMutation,
  BackfillMutationVariables,
  BackfillSourceBundle,
} from "../../graphql/types";
import { NameFromHandle } from "../common/Formatters";
import DateRangePicker from "../Date/DateRangePicker";
import { IfPermission } from "../Permissions";
import { Modal } from "../ui/Modal";
import { StatefulButton } from "../ui/muiButtons";

const ButtonPanel = styled("div")`
  padding: 0px;
`;

const BackfillPanel = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.surfaceLight10.main,
  padding: "20px",
  minHeight: "100px",
  display: "flex",
  flexDirection: "row",
  gap: "20px",
  gridTemplateColumns: "1fr 1fr 1fr 1fr",
  alignItems: "center",
}));

const ShowBackfillPanelButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => (
  <IfPermission permission={Permissions.ManagePipelines}>
    <Button
      id="backfillButton"
      onClick={onClick}
      data-cy="backfill-button"
      startIcon={<ClockCounterClockwiseIcon size={12} weight="bold" />}
    >
      Backfill Options
    </Button>
  </IfPermission>
);
const LaunchBackfillButton = ({
  loading,
  enabled,
  onClick,
}: {
  loading: boolean;
  enabled: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => (
  <StatefulButton
    loading={loading}
    id="launchBackfillButton"
    onClick={onClick}
    disabled={!enabled}
    data-cy="launch-backfill-button"
    startIcon={<CaretCircleRightIcon size={12} weight="fill" />}
  >
    Launch Backfill
  </StatefulButton>
);

const ExplainDateRangeBackfill = () => {
  const text = <Typography variant="body.xSmall.regular">Select Date Range to Backfill. Max 30 days.</Typography>;
  return <IconInfo text={text} />;
};

interface IValidateBackfill {
  dateRange: {
    start: Date;
    end: Date;
  };
  importName: string;
}

const validateBackfill = ({ dateRange, importName }: IValidateBackfill) => {
  const errors: string[] = [];
  if (dateRange && differenceInDays(dateRange.start, dateRange.end) > 30) {
    errors.push("Max Date Range is 30 days.");
  }

  if ((importName || "") === "") {
    errors.push("Select an Import.");
  }

  return { valid: errors.length === 0, errors };
};

const backfillVariables = (
  importName: string,
  dateRange: {
    start: Date;
    end: Date;
  }
) => {
  return {
    name: importName,
    since: `${format(dateRange.start, "yyyy-MM-dd")}T00:00:00`,
    until: `${format(dateRange.end, "yyyy-MM-dd")}T23:59:59`,
  };
};

const BackfillReportItems = ({ items }: { items: HandleType[] | null }) => {
  if (items && items.length > 0) {
    const file_items = items.map((item, index) => {
      return (
        <div key={index}>
          <NameFromHandle>{item}</NameFromHandle>
        </div>
      );
    });
    return <Stack direction="column">{file_items}</Stack>;
  } else {
    return <div>Nothing backfilled</div>;
  }
};

const ShowBackfillFormButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => (
  <ButtonPanel>
    <ShowBackfillPanelButton onClick={onClick} />
  </ButtonPanel>
);

type Filter = {
  dateRange: {
    start: Date;
    end: Date;
  };
  importName: string;
};

const BackfillForm = ({
  onCancel,
  onLaunchBackfill,
  loading,
}: {
  onCancel?: MouseEventHandler<HTMLButtonElement>;
  onLaunchBackfill: (filter: Filter) => void;
  loading: boolean;
}) => {
  const maxDate = endOfDay(new Date());
  const initialDateRange = DateRange.fromDays(0, toDate(maxDate)).toDateRange();
  const [filter, setFilter] = useState<Filter>({
    dateRange: initialDateRange,
    importName: "",
  });
  const [filtersAreLoaded, setFiltersAreLoaded] = useState(false);
  const validation = validateBackfill(filter);

  const onFilterChange = (key: string, value: unknown) => {
    const newFilter = { [key]: value };
    setFilter({ ...filter, ...newFilter });
  };

  const onImportChanged = (filterValue: { project?: string | null; importName?: string | null }) => {
    onFilterChange("importName", filterValue.importName);
  };

  const onFiltersLoaded = () => {
    setFiltersAreLoaded(true);
  };

  return (
    <BackfillPanel id="backfillPanel" data-cy="backfill-panel">
      <Stack sx={{ width: "215px" }} data-testid="date-range-view">
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Typography variant="body.xSmall.regular">Report Range</Typography>
          <ExplainDateRangeBackfill />
        </Stack>
        <DateRangePicker
          slots={{ field: SingleInputDateRangeField }}
          start={filter.dateRange.start}
          end={filter.dateRange.end}
          onChange={(value) => {
            onFilterChange("dateRange", {
              start: value[0],
              end: value[1],
            });
          }}
          maxDate={toDate(maxDate)}
          showShortcuts={false}
        />
      </Stack>
      <Grid>
        <FilterPublishedImports
          id="backfillImportSelect"
          importName={filter.importName}
          filterByBackFill={true}
          allowSelectProjects={false}
          allowSelectSuspended={false}
          onLoaded={onFiltersLoaded}
          onChange={onImportChanged}
        />
      </Grid>
      <Grid sx={{ marginTop: "24px" }}>
        <LaunchBackfillButton
          loading={loading}
          // error={error} // TODO: Implement mutation states to handle error props
          enabled={validation && validation.valid && filtersAreLoaded}
          onClick={() => onLaunchBackfill(filter)}
        />
      </Grid>
      <Grid sx={{ marginTop: "24px" }}>
        <CancelLinkButton onCancel={onCancel} />
      </Grid>
    </BackfillPanel>
  );
};

type BackfillSuccessProps = {
  importName: string;
  report: BackfillSourceBundle | null;
  open: boolean;
  onClose?: () => void;
};

const BackfillSuccess = ({ importName, report, open, onClose }: BackfillSuccessProps) => (
  <Modal
    open={open}
    onClose={onClose}
    className="sb-modal"
    header={
      <Typography
        id="contained-modal-title-vcenter"
        data-cy="modal-header"
        sx={{ fontSize: "22px", marginTop: 2, fontWeight: 500 }}
      >
        <ClockCounterClockwiseIcon size={12} weight="bold" />
        Backfill for {importName}
      </Typography>
    }
    body={<BackfillReportItems items={report?.handles as HandleType[]} />}
    footer={
      <Button id="closeBackfillModal" onClick={onClose} data-cy="close-backfill-modal">
        Close
      </Button>
    }
  />
);

type BackfillErrorProps = {
  importName: string;
  onCancel?: () => void;
  responseError: string;
};

const BackfillError = ({ importName, onCancel, responseError }: BackfillErrorProps) => {
  return (
    <Alert severity="error" onClose={onCancel} className={"backfillError"}>
      <AlertTitle>Problem backfilling Import {importName}.</AlertTitle>
      <div>
        The backfill failed with error reason: {responseError}
        <CancelLinkButton onCancel={onCancel} />
      </div>
    </Alert>
  );
};

interface BackfillOptsProps {
  importName: string;
}

interface BackfillWorkflowProps {
  onCloseForm?: () => void;
  mode: string;
  setMode: Dispatch<SetStateAction<string>>;
  backfills: BackfillSourceBundle | null;
  setBackfills: Dispatch<SetStateAction<BackfillSourceBundle | null>>;
  responseError: string;
  setResponseError: Dispatch<SetStateAction<string>>;
  backfillOpts: BackfillOptsProps;
  setBackfillOpts: Dispatch<SetStateAction<BackfillOptsProps>>;
}

const BackfillWorkflow: FC<BackfillWorkflowProps> = ({
  onCloseForm,
  mode,
  setMode,
  backfills,
  setBackfills,
  responseError,
  setResponseError,
  backfillOpts,
  setBackfillOpts,
}) => {
  const onPersistSuccess = (report: BackfillMutation) => {
    setMode("success");
    setBackfills(report.backfill_source_bundle || null);
  };
  const onPersistError = (error: Error) => {
    setResponseError(parseGQLResponseError(error));
    setMode("error");
  };
  const [persistBackfill, { loading }] = useMutation<BackfillMutation, BackfillMutationVariables>(BackfillDocument, {
    refetchQueries: [DOWNLOAD_FILES_QUERY],
    awaitRefetchQueries: false,
    onCompleted: onPersistSuccess,
    onError: onPersistError,
  });

  const onLaunchBackfill = (filter: Filter) => {
    const { importName, dateRange } = filter;
    setBackfillOpts(filter);
    const payload = {
      variables: backfillVariables(importName, dateRange),
    };
    persistBackfill(payload);
  };

  if (mode === "create") {
    return <BackfillForm onLaunchBackfill={onLaunchBackfill} onCancel={onCloseForm} loading={loading} />;
  } else if (mode === "success") {
    return (
      <BackfillSuccess
        importName={backfillOpts.importName}
        report={backfills}
        open={mode === "success"}
        onClose={onCloseForm}
      />
    );
  } else if (mode === "error") {
    return <BackfillError importName={backfillOpts.importName} onCancel={onCloseForm} responseError={responseError} />;
  } else {
    return null;
  }
};

const Backfill = () => {
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState("create");
  const [backfills, setBackfills] = useState<BackfillSourceBundle | null>(null);
  const [responseError, setResponseError] = useState("");
  const [backfillOpts, setBackfillOpts] = useState<BackfillOptsProps>({
    importName: "",
  });
  if (showForm) {
    return (
      <BackfillWorkflow
        key={new Date().toISOString()}
        onCloseForm={() => {
          setShowForm(false);
          setMode("create");
          setBackfills(null);
          setResponseError("");
        }}
        mode={mode}
        setMode={setMode}
        backfills={backfills}
        setBackfills={setBackfills}
        responseError={responseError}
        setResponseError={setResponseError}
        backfillOpts={backfillOpts}
        setBackfillOpts={setBackfillOpts}
      />
    );
  } else {
    return <ShowBackfillFormButton onClick={() => setShowForm(true)} />;
  }
};

export { Backfill, backfillVariables };
